import React, { useMemo } from 'react';
import { VirtualizedMultiSelectCombobox } from '@roger/ui-components';
import { useIntakeAssignableUsers } from '../hooks/useIntakeAssignableUsers';

export interface AssigneeFilterValue {
  userIds: string[];
  includeUnassigned: boolean;
}
interface IntakeAssigneeFilterProps {
  value: AssigneeFilterValue;
  onChange: (value: AssigneeFilterValue) => void;
}
const UNASSIGNED_OPTION_VALUE = '__unassigned__';
const UNASSIGNED_LABEL = 'Unassigned';
const EMPTY_LABEL = 'All Assignees';
const LOAD_ERROR_LABEL = "Couldn't load users";
const MAX_DISPLAY_ITEMS = 2;
const IntakeAssigneeFilter: React.FC<IntakeAssigneeFilterProps> = ({
  value,
  onChange,
}) => {
  const {
    options,
    isLoading: usersLoading,
    error: usersError,
  } = useIntakeAssignableUsers();
  const userIds = useMemo(
    () => [...new Set(value.userIds ?? [])],
    [value.userIds]
  );
  const selectedValues = useMemo(
    () => [
      ...(value.includeUnassigned ? [UNASSIGNED_OPTION_VALUE] : []),
      ...userIds,
    ],
    [value.includeUnassigned, userIds]
  );
  const handleValueChange = (next: string[]) => {
    onChange({
      includeUnassigned: next.includes(UNASSIGNED_OPTION_VALUE),
      userIds: next.filter(selected => selected !== UNASSIGNED_OPTION_VALUE),
    });
  };
  return (
    <VirtualizedMultiSelectCombobox
      options={options}
      sentinelOption={{
        value: UNASSIGNED_OPTION_VALUE,
        label: UNASSIGNED_LABEL,
      }}
      value={selectedValues}
      onValueChange={handleValueChange}
      placeholder={EMPTY_LABEL}
      searchPlaceholder='Search assignees…'
      ariaLabelPrefix='Assignee: '
      capAriaLabel
      buttonClassName='w-full'
      maxDisplayItems={MAX_DISPLAY_ITEMS}
      isLoading={usersLoading}
      loadingText='Loading users…'
      error={usersError ? LOAD_ERROR_LABEL : undefined}
      errorOptionLabel={LOAD_ERROR_LABEL}
      unknownOptionLabel='Unknown user'
    />
  );
};
export default IntakeAssigneeFilter;
